import { APP_TIMEZONE } from '@qlk/shared';

/**
 * Định dạng số và ngày giờ dùng chung cho mọi màn hình.
 *
 * Gom ở bước 12.2: trước đó mỗi module có bản sao riêng và **chúng lệch nhau** —
 * `gonSo(128000)` ra `"128,0K"` ở màn Quản lý kênh nhưng `"128K"` ở màn Phân
 * tích, còn ngày thì chỗ dùng `vi-VN` (ra `09-10`) chỗ lại ghép tay.
 */

/* ------------------------------------------------------------------ */
/* Số                                                                  */
/* ------------------------------------------------------------------ */

/**
 * Câu giải thích cho mọi ô `—` vì **thiếu lượt xem**, không vì lỗi.
 *
 * Một chuỗi dùng chung cho cả `/kenh`, Tổng quan và màn Phân tích: ba nơi nói
 * khác nhau thì người đọc tưởng ba nguyên nhân khác nhau. Xem
 * `docs/modules/sync.md` mục "Lượt xem thiếu là NULL".
 */
export const CHUA_DU_LUOT_XEM = 'Chưa đủ số liệu lượt xem';

/**
 * Câu giải thích cho mọi ô `—` vì **thiếu lượt hiển thị** (nên cũng thiếu CTR).
 *
 * Lượt hiển thị chỉ đến từ YouTube Reporting API (job `reach-daily`) và chỉ có
 * từ ngày app tạo reporting job, nên nhiều kỳ cũ không có số nào. Không hiện
 * `0 %`: đọc ra "không ai bấm vào thumbnail", trong khi app chưa biết gì. Xem
 * `docs/modules/sync.md` mục "Lượt hiển thị và CTR chỉ luuReach ghi".
 */
export const CHUA_CO_LUOT_HIEN_THI = 'Chưa có số liệu hiển thị';

/**
 * `128000 → "128,0K"`, `6420000 → "6,42M"`. Theo cách hiển thị trong ảnh thiết
 * kế (`378,4K` subscribers, `6,42M` lượt xem, `2,10M` lượt xem của một kênh).
 * `soLeTrieu` đổi số lẻ của mốc triệu khi cần gọn hơn.
 */
export function gonSo(giaTri: number | null | undefined, soLeTrieu = 2): string {
  if (giaTri === null || giaTri === undefined || !Number.isFinite(giaTri)) return '—';
  if (Math.abs(giaTri) >= 1_000_000) {
    return `${(giaTri / 1_000_000).toFixed(soLeTrieu).replace('.', ',')}M`;
  }
  if (Math.abs(giaTri) >= 1_000) return `${(giaTri / 1_000).toFixed(1).replace('.', ',')}K`;
  return giaTri.toLocaleString('vi-VN');
}

/** `1240 → "1.240"` theo cách viết tiếng Việt. */
export function soNguyen(giaTri: number | null | undefined): string {
  if (giaTri === null || giaTri === undefined || !Number.isFinite(giaTri)) return '—';
  return giaTri.toLocaleString('vi-VN');
}

/** `7.4 → "7,4%"`. Số âm giữ dấu trừ. */
export function phanTram(giaTri: number | null | undefined, soLe = 1): string {
  if (giaTri === null || giaTri === undefined || !Number.isFinite(giaTri)) return '—';
  return `${giaTri.toFixed(soLe).replace('.', ',')}%`;
}

/**
 * Như `phanTram` nhưng bỏ dấu trừ — dùng ở chỗ đã có mũi tên ▲ ▼ thể hiện hướng
 * (phần `delta` của `StatCard`).
 */
export function phanTramKhongDau(giaTri: number | null | undefined, soLe = 1): string {
  if (giaTri === null || giaTri === undefined || !Number.isFinite(giaTri)) return '—';
  return phanTram(Math.abs(giaTri), soLe);
}

/** Hướng của delta để chọn màu cho `StatCard`. */
export function huongDelta(delta: number | null | undefined): 'tang' | 'giam' | 'phang' {
  if (delta === null || delta === undefined || delta === 0) return 'phang';
  return delta > 0 ? 'tang' : 'giam';
}

/** `872` giây → `"14:32"`; `4210` giây → `"1:10:10"`. */
export function thoiLuong(giay: number | null | undefined): string {
  if (giay === null || giay === undefined || !Number.isFinite(giay)) return '—';

  const g = Math.max(Math.round(giay), 0);
  const gio = Math.floor(g / 3600);
  const phut = Math.floor((g % 3600) / 60);
  const con = g % 60;
  const hai = (n: number) => String(n).padStart(2, '0');

  return gio > 0 ? `${String(gio)}:${hai(phut)}:${hai(con)}` : `${String(phut)}:${hai(con)}`;
}

/* ------------------------------------------------------------------ */
/* Ngày giờ                                                            */
/* ------------------------------------------------------------------ */

/**
 * Mọi ngày giờ hiển thị theo giờ Việt Nam (CLAUDE.md).
 *
 * ⚠️ Không dùng thẳng `toLocaleDateString('vi-VN', { day, month })`: nó trả
 * `"09-10"` chứ không phải `"09/10"`. Vì vậy phải ghép tay từ `formatToParts`.
 */
const PHAN_NGAY = new Intl.DateTimeFormat('vi-VN', {
  timeZone: APP_TIMEZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const GIO_PHUT = new Intl.DateTimeFormat('vi-VN', {
  timeZone: APP_TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function tachNgay(iso: string | Date): { ngay: string; thang: string; nam: string } | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const phan = PHAN_NGAY.formatToParts(d);
  const lay = (ten: Intl.DateTimeFormatPartTypes) => phan.find((x) => x.type === ten)?.value ?? '';

  return { ngay: lay('day'), thang: lay('month'), nam: lay('year') };
}

/** `dd/MM` — dùng cho cột hạn, nhãn lịch, mô tả cảnh báo. */
export function ngayNgan(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const p = tachNgay(iso);
  return p ? `${p.ngay}/${p.thang}` : '—';
}

/** `dd/MM/yyyy` — dùng cho bảng và trang chi tiết. */
export function ngayDay(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const p = tachNgay(iso);
  return p ? `${p.ngay}/${p.thang}/${p.nam}` : '—';
}

/** `HH:mm` theo giờ Việt Nam. */
export function gioPhut(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : GIO_PHUT.format(d);
}

/** `dd/MM/yyyy HH:mm`. */
export function ngayGio(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${ngayDay(d)} ${GIO_PHUT.format(d)}`;
}

/** `dd/MM HH:mm` — bảng hẹp không đủ chỗ cho cả năm. */
export function ngayGioNgan(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${ngayNgan(d)} ${GIO_PHUT.format(d)}`;
}

/** Số ngày còn lại tới `iso` (âm là đã qua). `null` khi không có ngày. */
export function soNgayConLai(iso: string | null | undefined, bayGio = Date.now()): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - bayGio) / 86_400_000);
}

const KHOA_NGAY_VN = new Intl.DateTimeFormat('en-CA', {
  timeZone: APP_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Hôm nay theo giờ Việt Nam, dạng `YYYY-MM-DD`.
 *
 * Dùng cho `<input type="date">`: `new Date().toISOString()` lấy theo UTC nên
 * sau 7 giờ tối giờ Việt Nam sẽ ra ngày hôm trước.
 */
export function homNayVn(): string {
  return KHOA_NGAY_VN.format(new Date());
}
